import {
  STATUS_COLORS,
  DIFFICULTY_LABELS,
  getRemainingToMaster,
  getMasteryPercent
} from './knowledgeMapUtils'

export default function MasteryTooltip({ node }) {
  const mastery = node.mastery || { status: 'UNVISITED', score: 0 }
  const colors = STATUS_COLORS[mastery.status]
  const prereqs = node.prerequisites || []
  const allNodes = node._allNodes || []
  const diffLabel = DIFFICULTY_LABELS[node.difficulty] || node.difficulty

  const score = mastery.score || 0
  const masteryPct = getMasteryPercent(score)
  const { label: remainingLabel } = getRemainingToMaster(score, node.difficulty, mastery.status)

  const prereqStatuses = prereqs.map((prereqKey) => {
    const prereqNode = allNodes.find((n) => n.skillGroupKey === prereqKey)
    return {
      skill: prereqNode?.skill || prereqKey,
      difficulty: prereqNode?.difficulty,
      status: prereqNode?.mastery?.status || 'UNVISITED'
    }
  })
  const allPrereqsMastered =
    prereqStatuses.length === 0 || prereqStatuses.every((p) => p.status === 'MASTERED')

  return (
    <div className="absolute top-4 right-4 z-50 bg-white rounded-lg shadow-xl border p-4 w-72">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.dot }} />
        <span className="font-semibold text-sm">{node.skill}</span>
        <span className="text-xs text-gray-500">({diffLabel})</span>
      </div>

      {/* Mastery progress — primary info */}
      {mastery.totalAttempts > 0 ? (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-600 font-medium">Mastery</span>
            {mastery.status === 'MASTERED' ? (
              <span className="text-green-600 font-semibold">Mastered!</span>
            ) : (
              <span className="font-mono text-gray-700">{(score * 100).toFixed(0)}% / 70%</span>
            )}
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${masteryPct}%`, backgroundColor: colors.dot }}
            />
          </div>
          {remainingLabel && mastery.status !== 'MASTERED' && (
            <div className="text-[11px] text-gray-500 mt-1">{remainingLabel}</div>
          )}
        </div>
      ) : (
        <div className="text-xs text-gray-500 mb-3">
          Status:{' '}
          <span className="font-medium" style={{ color: colors.border }}>
            {colors.text}
          </span>
        </div>
      )}

      {/* Correct answers — secondary info */}
      {mastery.totalAttempts > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
          <span>Correct:</span>
          <span className="font-mono">
            {mastery.correctAttempts}/{mastery.totalAttempts}
            <span className="ml-1">
              ({Math.round((mastery.correctAttempts / mastery.totalAttempts) * 100)}%)
            </span>
          </span>
        </div>
      )}

      {mastery.lastAttemptAt && (
        <div className="text-[11px] text-gray-400">
          Last: {new Date(mastery.lastAttemptAt).toLocaleDateString()}
        </div>
      )}

      {prereqs.length > 0 && (
        <div className="mt-2 pt-2 border-t text-xs">
          <span className="text-gray-500">Prerequisites: </span>
          {allPrereqsMastered ? (
            <span className="text-green-600 font-medium">All mastered</span>
          ) : (
            <div className="mt-1 space-y-0.5">
              {prereqStatuses
                .filter((p) => p.status !== 'MASTERED')
                .map((p) => (
                  <div key={p.skill + (p.difficulty || '')} className="text-amber-600">
                    {p.skill}
                    {p.difficulty ? ` [${p.difficulty}]` : ''} (
                    {p.status.toLowerCase().replace('_', ' ')})
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {node.hasFocusedPractice && (
        <div className="mt-2 pt-2 border-t">
          <a
            href={`/focused-practice/${node.focusedPracticeId}/review`}
            className="text-xs text-primary-600 hover:underline"
          >
            Focused Practice pending
          </a>
        </div>
      )}
    </div>
  )
}
